import { useCallback, useState } from "react";
import {
  ToolContainer,
  FileDropZone,
  DownloadButton,
  downloadBlob,
} from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import JSZip from "jszip";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { MAX_EXTRUDE, MAX_PADDING, atlasJson, drawSheet, frameNames, framesLabel, parseWhole, planSheet, sheetProblem, splitCells } from "./spriteAtlas";

const TOOL_INFO_CONTENT = {
  what: `Combines multiple individual images into a single sprite sheet grid, or splits an existing sprite sheet back into individual frame images — useful for game development and animation work.`,
  how: `In combine mode, upload your frames in order and set the grid columns; in split mode, upload a sprite sheet and specify its rows and columns. Download the result.`,
  tip: `Keep all your source frames the same pixel dimensions before combining them into a sheet — mismatched frame sizes will throw off the grid alignment in the combined result.`,
};

export default function SpriteSheetTool() {
  const [mode, setMode] = useState<"combine" | "split">("combine");

  // Combine mode
  const [frames, setFrames] = useState<File[]>([]);
  const [columns, setColumns] = useState("4");
  const [combinedUrl, setCombinedUrl] = useState<string | null>(null);
  const [combinedBlob, setCombinedBlob] = useState<Blob | null>(null);
  const [padding, setPadding] = useState("0");
  const [extrude, setExtrude] = useState("0");
  const [wantJson, setWantJson] = useState(false);
  const [atlasBlob, setAtlasBlob] = useState<Blob | null>(null);

  // Split mode
  const [sheet, setSheet] = useState<File | null>(null);
  const [sheetCols, setSheetCols] = useState("4");
  const [sheetRows, setSheetRows] = useState("2");
  const [sheetPadding, setSheetPadding] = useState("0");
  const [splitExact, setSplitExact] = useState(true);
  const [splitFrames, setSplitFrames] = useState<{ url: string; blob: Blob }[]>(
    []
  );
  const { logUsageNow } = useLogUsageOnAction();

  const handleFramesUpload = useCallback((files: File[]) => {
    setFrames(files);
    setCombinedUrl(null);
    setCombinedBlob(null);
    setAtlasBlob(null);
  }, []);

  const handleSheetUpload = useCallback((files: File[]) => {
    const f = files[0];
    if (!f) return;
    setSheet(f);
    setSplitFrames([]);
  }, []);

  const combine = async () => {
    if (frames.length === 0) return;
    const cols = Math.max(1, parseInt(columns, 10) || 1);

    let images: HTMLImageElement[];
    try { images = await Promise.all(
      frames.map(
        f =>
          new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Couldn't read ${f.name}`));
            img.src = URL.createObjectURL(f);
          })
      )
    ); } catch (e) { toast.error(e instanceof Error ? e.message : "One of the frames couldn't be read"); return; }

    // Each frame keeps its own size and sits centred in its cell (stretching distorted mixed-size sprites).
    // Padding goes between cells and extrude repeats each frame's edge pixels outwards; both default to 0 = the plain grid.
    const names = frameNames(frames.map(f => f.name));
    const plan = planSheet(
      images.map((img, i) => ({ name: names[i], width: img.naturalWidth, height: img.naturalHeight })),
      { cols, padding: parseWhole(padding, 0, MAX_PADDING), extrude: parseWhole(extrude, 0, MAX_EXTRUDE) },
    );
    const problem = sheetProblem(plan);
    if (problem) { toast.error(problem); return; }

    // Logged here -- the actual combine action, after the frames were read and the sheet is known to be drawable -- not
    // on each frame drop: adding frames across separate drops no longer burns extra uses for one unfinished sprite
    // sheet, and a frame that cannot be read or a sheet that is too big costs nothing.
    const allowed = await logUsageNow();
    if (!allowed) return;

    const canvas = document.createElement("canvas");
    canvas.width = plan.width;
    canvas.height = plan.height;
    const ctx = canvas.getContext("2d")!;
    drawSheet(ctx, plan, images);

    canvas.toBlob(blob => {
      if (!blob) return;
      setCombinedBlob(blob);
      setCombinedUrl(URL.createObjectURL(blob));
    }, "image/png");
    setAtlasBlob(new Blob([JSON.stringify(atlasJson(plan, "sprite-sheet.png"), null, 2)], { type: "application/json" }));

    toast.success(
      `Combined ${framesLabel(frames.length)} into a ${plan.width} × ${plan.height} px sprite sheet (${plan.cols}×${plan.rows})`
    );
  };

  const split = async () => {
    if (!sheet) return;
    const cols = Math.max(1, parseInt(sheetCols, 10) || 1);
    const rows = Math.max(1, parseInt(sheetRows, 10) || 1);

    const img = new Image();
    img.src = URL.createObjectURL(sheet);
    await new Promise(r => (img.onload = r));

    const { cells, exact } = splitCells(img.naturalWidth, img.naturalHeight, cols, rows, parseWhole(sheetPadding, 0, MAX_PADDING));
    const results: { url: string; blob: Blob }[] = [];

    for (const c of cells) {
      const canvas = document.createElement("canvas");
      canvas.width = c.w;
      canvas.height = c.h;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, c.x, c.y, c.w, c.h, 0, 0, c.w, c.h);
      const blob = await new Promise<Blob>(resolve =>
        canvas.toBlob(b => resolve(b!), "image/png")
      );
      results.push({ url: URL.createObjectURL(blob), blob });
    }
    setSplitFrames(results);
    setSplitExact(exact);
    toast.success(`Split into ${framesLabel(results.length)}`);
  };

  const downloadAllFrames = async () => {
    const zip = new JSZip();
    splitFrames.forEach((f, i) =>
      zip.file(`frame_${String(i + 1).padStart(2, "0")}.png`, f.blob)
    );
    const content = await zip.generateAsync({ type: "blob" });
    downloadBlob(content, "sprite-frames.zip");
  };

  return (
    <ToolContainer>
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          variant={mode === "combine" ? "default" : "outline"}
          onClick={() => setMode("combine")}
        >
          Combine Frames → Sheet
        </Button>
        <Button
          variant={mode === "split" ? "default" : "outline"}
          onClick={() => setMode("split")}
        >
          Split Sheet → Frames
        </Button>
      </div>

      {mode === "combine" ? (
        <div className="space-y-4">
          <FileDropZone
            accept="image/*"
            multiple
            onFiles={handleFramesUpload}
            label="Drop multiple frame images"
            description="Upload frames in order — they'll be arranged left-to-right, top-to-bottom"
            deferUsageLog
          />
          {frames.length > 0 && (
            <>
              <p className="text-sm text-muted-foreground">
                {frames.length} frames loaded
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-sm">Columns</Label>
                <Input aria-label="Columns"
                  className="w-20"
                  value={columns}
                  onChange={e => setColumns(e.target.value)}
                />
                <Label className="text-sm">Padding (px)</Label>
                <Input aria-label="Padding (px)" inputMode="numeric" className="w-20" value={padding} onChange={e => setPadding(e.target.value)} />
                <Label className="text-sm">Extrude (px)</Label>
                <Input aria-label="Extrude (px)" inputMode="numeric" className="w-20" value={extrude} onChange={e => setExtrude(e.target.value)} />
                <Button onClick={combine}>Generate Sprite Sheet</Button>
              </div>
              <p className="text-xs text-muted-foreground">Padding leaves a gap between frames (up to {MAX_PADDING}); extrude repeats each frame's edge pixels outwards (up to {MAX_EXTRUDE}) so game engines do not bleed neighbours into a sprite. Both 0 gives the plain grid.</p>
              <div className="flex items-center gap-2">
                <Checkbox id="sprite-json" checked={wantJson} onCheckedChange={v => setWantJson(v === true)} />
                <Label htmlFor="sprite-json" className="font-normal cursor-pointer">Also offer a JSON atlas (each frame's x, y, width, height)</Label>
              </div>
            </>
          )}
          {combinedUrl && (
            <Card className="p-4">
              <img
                src={combinedUrl}
                alt="Sprite sheet"
                className="max-w-full border rounded mb-3"
              />
              <DownloadButton
                onClick={() => {
                  if (combinedBlob) downloadBlob(combinedBlob, "sprite-sheet.png");
                }}
                label="Download PNG"
              />
              {wantJson && atlasBlob && (
                <DownloadButton
                  onClick={() => downloadBlob(atlasBlob, "sprite-sheet.json")}
                  label="Download JSON atlas"
                />
              )}
            </Card>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <FileDropZone
            accept="image/*"
            gateAtDownload
            onFiles={handleSheetUpload}
            label="Drop a sprite sheet image"
            description="We'll slice it into a grid of individual frames"
          />
          {sheet && (
            <div className="flex flex-wrap items-center gap-3">
              <Label className="text-sm">Columns</Label>
              <Input aria-label="Columns"
                className="w-20"
                value={sheetCols}
                onChange={e => setSheetCols(e.target.value)}
              />
              <Label className="text-sm">Rows</Label>
              <Input aria-label="Rows"
                className="w-20"
                value={sheetRows}
                onChange={e => setSheetRows(e.target.value)}
              />
              <Label className="text-sm">Padding (px)</Label>
              <Input aria-label="Padding between frames (px)" inputMode="numeric" className="w-20" value={sheetPadding} onChange={e => setSheetPadding(e.target.value)} />
              <Button onClick={split}>Split into Frames</Button>
            </div>
          )}
          {splitFrames.length > 0 && (
            <>
              <p role="status" className="text-sm font-medium" data-testid="split-count">{framesLabel(splitFrames.length)}</p>
              {!splitExact && <p className="text-xs text-muted-foreground">The sheet size does not divide evenly by these columns, rows and padding, so frames were cut at rounded sizes. Check the numbers if the frames look off.</p>}
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {splitFrames.map((f, i) => (
                  <img
                    key={i}
                    src={f.url}
                    alt={`Frame ${i + 1}`}
                    className="border rounded w-full"
                  />
                ))}
              </div>
              <Button variant="outline" onClick={downloadAllFrames}>
                Download All as ZIP
              </Button>
            </>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
